import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { AddUsersDialog, GroupCheckboxes, RecipientFormDialog } from './RecipientDialogs';
import {
  bulkUpdateRecipients, createManualRecipient, createUserRecipients, deleteRecipient, fetchCandidates,
  fetchConnectGroups, fetchRecipients, updateRecipient,
} from './recipientsApi';
import {
  CONSENT_LABELS, KIND_LABELS, SKIP_REASONS, STATUS_FILTERS, errorText, filterRecipients, summarizeAddResult,
} from './recipientsModel';

// Zelfstandige verzendlijst: een ontvanger heeft geen Zoea-moduletoegang nodig en geen connectgroep.
const RecipientsPanel = () => {
  const { hasCapability } = useAuth();
  const canSeeAllUsers = hasCapability('organization_admin');
  const [recipients, setRecipients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [status, setStatus] = useState('alle');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addUsersOpen, setAddUsersOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkGroupIds, setBulkGroupIds] = useState([]);

  const load = useCallback(async () => {
    try {
      const [list, groupList] = await Promise.all([fetchRecipients(), fetchConnectGroups().catch(() => [])]);
      setRecipients(list);
      setGroups(groupList);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorText(error, 'Kon de ontvangers niet laden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => filterRecipients(recipients, { status, search }), [recipients, status, search]);
  const visibleIds = visible.map((recipient) => recipient.id);
  const allSelected = visible.length > 0 && visibleIds.every((id) => selected.includes(id));

  const run = async (action, successMessage) => {
    if (busy) return false;
    setBusy(true);
    try {
      const result = await action();
      if (successMessage) toast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
      await load();
      return true;
    } catch (error) {
      toast.error(errorText(error, 'Actie mislukt'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openAddUsers = async (all = showAll) => {
    try {
      setCandidates(await fetchCandidates(all));
      setShowAll(all);
      setAddUsersOpen(true);
    } catch (error) {
      toast.error(errorText(error, 'Kon de gebruikers niet laden'));
    }
  };

  const addUsers = async (userIds, groupIds) => {
    const ok = await run(async () => {
      const result = await createUserRecipients(userIds, groupIds);
      if (result.skipped.length) {
        toast.warning(result.skipped.map((item) => SKIP_REASONS[item.reason] || item.reason).join(', '));
      }
      return result;
    }, summarizeAddResult);
    if (ok) setAddUsersOpen(false);
  };

  const saveForm = async (form) => {
    const payload = editing
      ? (editing.kind === 'user'
        ? { group_ids: form.group_ids, email_consent: form.email_consent }
        : { name: form.name, email: form.email, group_ids: form.group_ids, email_consent: form.email_consent })
      : { name: form.name, email: form.email, group_ids: form.group_ids, email_consent: form.email_consent };
    const ok = await run(
      () => (editing ? updateRecipient(editing.id, payload) : createManualRecipient(payload)),
      editing ? 'Ontvanger bijgewerkt' : 'Ontvanger toegevoegd',
    );
    if (ok) { setFormOpen(false); setEditing(null); }
  };

  const bulk = async (action, groupIds = []) => {
    const ok = await run(() => bulkUpdateRecipients(selected, action, groupIds), 'Ontvangers bijgewerkt');
    if (ok) setSelected([]);
  };

  const remove = async (recipient) => {
    if (!window.confirm(`${recipient.name} van de verzendlijst verwijderen? Het Zoea-account blijft bestaan.`)) return;
    await run(() => deleteRecipient(recipient.id), 'Ontvanger verwijderd');
    setSelected((current) => current.filter((id) => id !== recipient.id));
  };

  if (loading) return <div className="h-40 animate-pulse rounded-xl bg-slate-200" />;

  return (
    <div className="space-y-4" data-testid="recipients-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-sm text-slate-500">
          Wie de connectles ontvangt. Ontvangers hoeven geen Zoea-account of connectgroep te hebben en krijgen via deze lijst
          geen toegang tot andere modules.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openAddUsers()}><UserPlus className="mr-2 h-4 w-4" />Zoea-gebruikers</Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-fuchsia-600 text-white hover:bg-fuchsia-700"><Plus className="mr-2 h-4 w-4" />Externe ontvanger</Button>
        </div>
      </div>

      {loadError && <p role="alert" className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{loadError}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Zoek ontvangers" className="max-w-xs" placeholder="Zoek op naam, e-mailadres of groep" value={search} onChange={(event) => setSearch(event.target.value)} />
        {STATUS_FILTERS.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={status === value} onClick={() => setStatus(value)}
            className={`min-h-9 rounded-full border px-3 text-sm font-medium ${status === value ? 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800' : 'border-slate-200 text-slate-600'}`}>{label}</button>
        ))}
        <span className="text-sm text-slate-500">{visible.length} van {recipients.length}</span>
      </div>

      {selected.length > 0 && (
        <div className="space-y-3 rounded-xl border border-fuchsia-200 bg-fuchsia-50/50 p-3" data-testid="bulk-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-800">{selected.length} geselecteerd</span>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bulk('activate')}>Activeren</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => bulk('deactivate')}>Deactiveren</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Selectie wissen</Button>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-slate-600">Connectgroep voor de selectie instellen (leeg = geen koppeling)</p>
            <GroupCheckboxes groups={groups} value={bulkGroupIds} onChange={setBulkGroupIds} />
            <Button size="sm" disabled={busy} onClick={() => bulk('set_groups', bulkGroupIds)}>Groepen toepassen</Button>
          </div>
        </div>
      )}

      {visible.length > 0 && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={allSelected} onCheckedChange={(checked) => setSelected(checked ? [...new Set([...selected, ...visibleIds])] : selected.filter((id) => !visibleIds.includes(id)))} aria-label="Selecteer alle zichtbare ontvangers" />
          Alles selecteren
        </label>
      )}

      <div className="space-y-2">
        {visible.map((recipient) => (
          <div key={recipient.id} data-testid="recipient-row" className={`flex items-start gap-3 rounded-xl border bg-white p-3 shadow-sm ${recipient.is_active ? 'border-slate-200' : 'border-slate-200 opacity-70'}`}>
            <Checkbox className="mt-1" checked={selected.includes(recipient.id)} aria-label={`Selecteer ${recipient.name}`}
              onCheckedChange={(checked) => setSelected((current) => (checked ? [...current, recipient.id] : current.filter((id) => id !== recipient.id)))} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-medium text-slate-900">{recipient.name || 'Onbekend'}</p>
                <Badge variant="secondary">{KIND_LABELS[recipient.kind]}</Badge>
                {!recipient.is_active && <Badge variant="outline">Inactief</Badge>}
                {!recipient.account_available && <Badge variant="outline">Account niet beschikbaar</Badge>}
              </div>
              <p className="flex items-center gap-1 truncate text-sm text-slate-500"><Mail className="h-3.5 w-3.5 shrink-0" />{recipient.email || '—'}</p>
              <p className="mt-1 text-xs text-slate-500">
                {CONSENT_LABELS[recipient.email_consent]}
                {recipient.groups.length > 0 && ` · ${recipient.groups.map((group) => group.name).join(', ')}`}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button variant="ghost" size="icon" aria-label={`Bewerk ${recipient.name}`} onClick={() => { setEditing(recipient); setFormOpen(true); }}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" aria-label={`Verwijder ${recipient.name}`} onClick={() => remove(recipient)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
        {!visible.length && (
          <div className="py-12 text-center">
            <Mail className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="font-medium text-slate-800">{recipients.length ? 'Geen ontvangers gevonden' : 'Nog geen ontvangers'}</p>
            <p className="mt-1 text-sm text-slate-500">{recipients.length ? 'Pas de zoekopdracht of het filter aan.' : 'Voeg Zoea-gebruikers of externe e-mailadressen toe.'}</p>
          </div>
        )}
      </div>

      <AddUsersDialog open={addUsersOpen} onOpenChange={setAddUsersOpen} candidates={candidates} canSeeAll={canSeeAllUsers}
        showAll={showAll} onToggleAll={openAddUsers} groups={groups} onSubmit={addUsers} busy={busy} />
      <RecipientFormDialog open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) setEditing(null); }}
        recipient={editing} groups={groups} onSubmit={saveForm} busy={busy} />
    </div>
  );
};

export default RecipientsPanel;
